/**
 * Compliance Email Triggers (Phase 2)
 *
 * Four trigger types, one config card each (one trigger per type per AMC):
 *   • monthly_list      — sent on day N of every month, listing that month's due compliances
 *   • month_end_status  — sent on day N; covers current or previous month
 *   • annual_calendar   — sent once a year on month/day; calendar (Jan–Dec) or financial (Apr–Mar)
 *   • as_of_date        — manual: pick from/to dates and Send Now
 *
 * Scheduled triggers are fired by the platform cron (daily 7:30am IST scan);
 * the email goes out in the background to the Compliance Recipients library
 * (or a per-trigger subset). Each send is resume-safe.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronLeft, Loader, Send, Save, CheckCircle, Clock } from 'lucide-react';
import { complianceAPI, fundAPI } from '../services/api';

const TYPE_META = {
  monthly_list: {
    title: 'Compliance for the Month',
    desc: 'Sent on the chosen day each month, listing all fund-wise compliances due in that month. PDF + Excel attached.',
  },
  month_end_status: {
    title: 'Compliance Status of the Month',
    desc: 'Sent on the chosen day — covers the current month, or the month that just ended. PDF + Excel attached.',
  },
  as_of_date: {
    title: 'Compliance Status (As of Date)',
    desc: 'Ad-hoc: pick a from/to date range and send instantly. Not scheduled. PDF + Excel attached.',
  },
  annual_calendar: {
    title: 'Annual Compliance Calendar',
    desc: 'Sent once a year on the chosen month/day, covering the full calendar or financial year. PDF + Excel attached.',
  },
  overdue_digest: {
    title: 'Overdue / Not Complied Digest',
    desc: 'Recap of every item past its internal or regulatory due date (still not complied). PDF + Excel attached.',
  },
};
const TYPE_ORDER = ['monthly_list', 'month_end_status', 'overdue_digest', 'as_of_date', 'annual_calendar'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ComplianceEmailTriggers = () => {
  const [triggers, setTriggers] = useState({});   // trigger_type -> doc
  const [runs, setRuns] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [funds, setFunds] = useState([]);
  const [forms, setForms] = useState({});          // trigger_type -> form state
  const [saving, setSaving] = useState(null);
  const [sending, setSending] = useState(null);
  const [msg, setMsg] = useState(null);
  const [asOf, setAsOf] = useState({ from_date: '', to_date: '' });

  const fetchAll = async () => {
    const [t, r, f] = await Promise.all([
      complianceAPI.listEmailTriggers(),
      complianceAPI.listComplianceRecipients(),
      fundAPI.getAll(),
    ]);
    const map = {};
    (t.data.triggers || []).forEach(tr => { map[tr.trigger_type] = tr; });
    setTriggers(map);
    setRuns(t.data.recent_runs || []);
    setRecipients(r.data.recipients || []);
    setFunds(f.data || []);
    const formMap = {};
    TYPE_ORDER.forEach(type => {
      const tr = map[type];
      formMap[type] = {
        send_day: tr?.send_day || 1,
        send_month: tr?.send_month || 1,
        month_offset: tr?.month_offset ?? 0,
        year_type: tr?.year_type || 'calendar',
        frequency: tr?.frequency || 'weekly',
        send_weekday: tr?.send_weekday ?? 0,
        fund_id: tr?.fund_id || '',
        recipient_user_ids: tr?.recipient_user_ids || [],
        enabled: tr?.enabled ?? true,
      };
    });
    setForms(formMap);
  };

  useEffect(() => { fetchAll(); }, []);

  const setForm = (type, patch) => setForms(f => ({ ...f, [type]: { ...f[type], ...patch } }));

  const toggleRecipient = (type, uid) => {
    const cur = forms[type]?.recipient_user_ids || [];
    setForm(type, {
      recipient_user_ids: cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid],
    });
  };

  const handleSave = async (type) => {
    setSaving(type);
    setMsg(null);
    try {
      await complianceAPI.saveEmailTrigger({ trigger_type: type, ...forms[type], fund_id: forms[type].fund_id || null });
      setMsg(`${TYPE_META[type].title} saved`);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const handleSendNow = async (type) => {
    setSending(type);
    try {
      // Ensure config is persisted first so recipients/scope are current
      const saved = await complianceAPI.saveEmailTrigger({ trigger_type: type, ...forms[type], fund_id: forms[type].fund_id || null });
      const body = type === 'as_of_date' ? asOf : {};
      const res = await complianceAPI.sendEmailTriggerNow(saved.data.id, body);
      setMsg(`${TYPE_META[type].title}: ${res.data.message} (${res.data.range?.[0]} → ${res.data.range?.[1]})`);
      setTimeout(fetchAll, 4000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(null);
      setTimeout(() => setMsg(null), 5000);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="compliance-email-triggers-page">
      <div className="mb-6">
        <Link to="/compliance" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-1">
          <ChevronLeft size={14} /> Back to Tracker
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 inline-flex items-center gap-2">
          <Mail size={26} className="text-blue-600" /> Compliance Email Triggers
        </h1>
        <p className="text-gray-600 mt-1">
          Scheduled triggers fire automatically (daily 7:30am IST scan). Recipients default to the full
          Compliance Recipients library — pick specific members to restrict. Emails are sent in the background.
        </p>
      </div>

      {msg && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg px-4 py-2 inline-flex items-center gap-2">
          <CheckCircle size={14} /> {msg}
        </div>
      )}

      {TYPE_ORDER.map(type => {
        const meta = TYPE_META[type];
        const trg = triggers[type];
        const form = forms[type];
        if (!form) return null;
        return (
          <div key={type} className="bg-white border rounded-lg mb-4 overflow-hidden" data-testid={`trigger-card-${type}`}>
            <div className="px-5 py-3 bg-gray-50 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{meta.title}</h3>
                <p className="text-xs text-gray-500">{meta.desc}</p>
              </div>
              {type !== 'as_of_date' && (
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.enabled}
                         onChange={e => setForm(type, { enabled: e.target.checked })}
                         data-testid={`trigger-enabled-${type}`} />
                  <span className={form.enabled ? 'text-emerald-700 font-medium' : 'text-gray-500'}>
                    {form.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              )}
            </div>

            <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Schedule config */}
              <div className="space-y-3">
                {type === 'monthly_list' && (
                  <DayPicker label="Send on day (1–28) of every month" value={form.send_day}
                             onChange={v => setForm(type, { send_day: v })} testId={`send-day-${type}`} />
                )}
                {type === 'month_end_status' && (
                  <>
                    <DayPicker label="Send on day (1–28) of the month" value={form.send_day}
                               onChange={v => setForm(type, { send_day: v })} testId={`send-day-${type}`} />
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Report covers</label>
                      <select value={form.month_offset} onChange={e => setForm(type, { month_offset: Number(e.target.value) })}
                              className="border rounded px-2 py-1.5 text-sm w-full" data-testid={`month-offset-${type}`}>
                        <option value={0}>Current month</option>
                        <option value={-1}>Previous month (just ended)</option>
                      </select>
                    </div>
                  </>
                )}
                {type === 'annual_calendar' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Send in month</label>
                      <select value={form.send_month} onChange={e => setForm(type, { send_month: Number(e.target.value) })}
                              className="border rounded px-2 py-1.5 text-sm w-full" data-testid={`send-month-${type}`}>
                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                    <DayPicker label="On day (1–28)" value={form.send_day}
                               onChange={v => setForm(type, { send_day: v })} testId={`send-day-${type}`} />
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Year scheme</label>
                      <select value={form.year_type} onChange={e => setForm(type, { year_type: e.target.value })}
                              className="border rounded px-2 py-1.5 text-sm w-full" data-testid={`year-type-${type}`}>
                        <option value="calendar">Calendar Year (Jan–Dec)</option>
                        <option value="financial">Financial Year (Apr–Mar)</option>
                      </select>
                    </div>
                  </>
                )}
                {type === 'as_of_date' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">From date</label>
                      <input type="date" value={asOf.from_date} onChange={e => setAsOf(s => ({ ...s, from_date: e.target.value }))}
                             className="border rounded px-2 py-1.5 text-sm w-full" data-testid="as-of-from" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">To date</label>
                      <input type="date" value={asOf.to_date} onChange={e => setAsOf(s => ({ ...s, to_date: e.target.value }))}
                             className="border rounded px-2 py-1.5 text-sm w-full" data-testid="as-of-to" />
                    </div>
                  </>
                )}
                {type === 'overdue_digest' && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
                      <select value={form.frequency} onChange={e => setForm(type, { frequency: e.target.value })}
                              className="border rounded px-2 py-1.5 text-sm w-full" data-testid={`frequency-${type}`}>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    {form.frequency === 'weekly' ? (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Send every</label>
                        <select value={form.send_weekday} onChange={e => setForm(type, { send_weekday: Number(e.target.value) })}
                                className="border rounded px-2 py-1.5 text-sm w-full" data-testid={`send-weekday-${type}`}>
                          {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                      </div>
                    ) : (
                      <DayPicker label="Send on day (1–28) of every month" value={form.send_day}
                                 onChange={v => setForm(type, { send_day: v })} testId={`send-day-${type}`} />
                    )}
                  </>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fund scope (optional)</label>
                  <select value={form.fund_id} onChange={e => setForm(type, { fund_id: e.target.value })}
                          className="border rounded px-2 py-1.5 text-sm w-full" data-testid={`fund-scope-${type}`}>
                    <option value="">All funds</option>
                    {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Recipients */}
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Recipients <span className="text-gray-400">(none selected = all Compliance Recipients)</span>
                </label>
                {recipients.length === 0 ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Your Compliance Recipients library is empty. Add members from User Management → Compliance Recipients.
                  </p>
                ) : (
                  <div className="border rounded-lg p-2 max-h-44 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid={`recipients-${type}`}>
                    {recipients.map(r => (
                      <label key={r.user_id} className="inline-flex items-center gap-2 text-sm px-2 py-1 hover:bg-gray-50 rounded">
                        <input type="checkbox"
                               checked={(form.recipient_user_ids || []).includes(r.user_id)}
                               onChange={() => toggleRecipient(type, r.user_id)}
                               data-testid={`recipient-${type}-${r.user_id}`} />
                        <span>{r.name} <span className="text-gray-400 text-xs">({r.email})</span></span>
                      </label>
                    ))}
                  </div>
                )}
                {trg?.last_sent_period && (
                  <p className="text-xs text-gray-500 mt-2 inline-flex items-center gap-1">
                    <Clock size={12} /> Last sent for period <b>{trg.last_sent_period}</b>
                    {trg.last_sent_at && <> at {new Date(trg.last_sent_at).toLocaleString()}</>}
                  </p>
                )}
              </div>
            </div>

            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
              <button onClick={() => handleSave(type)} disabled={saving === type}
                      className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-100 inline-flex items-center gap-1.5"
                      data-testid={`save-trigger-${type}`}>
                {saving === type ? <Loader size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
              <button onClick={() => handleSendNow(type)}
                      disabled={sending === type || (type === 'as_of_date' && (!asOf.from_date || !asOf.to_date))}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`send-now-${type}`}>
                {sending === type ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
                Send Now
              </button>
            </div>
          </div>
        );
      })}

      {/* Recent runs */}
      {runs.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="trigger-runs">
          <div className="px-5 py-3 bg-gray-50 border-b font-semibold text-sm">Recent sends</div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-xs text-gray-500">
              <th className="text-left px-5 py-2">Period</th>
              <th className="text-left px-5 py-2">Sent</th>
              <th className="text-left px-5 py-2">Failed</th>
              <th className="text-left px-5 py-2">Finished</th>
            </tr></thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="px-5 py-2 font-mono text-xs">{r.period}</td>
                  <td className="px-5 py-2 text-emerald-700">{(r.sent || []).length}</td>
                  <td className="px-5 py-2 text-red-700">{(r.failed || []).length}</td>
                  <td className="px-5 py-2 text-gray-500 text-xs">{r.finished_at ? new Date(r.finished_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DayPicker = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <select value={value} onChange={e => onChange(Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-sm w-full" data-testid={testId}>
      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
    </select>
  </div>
);

export default ComplianceEmailTriggers;
